import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Icon from '../../components/AppIcon';
import api from '../../utils/api';
import { BookOpen, ChevronRight, Layers, FileText, ArrowLeft, Lightbulb, Clock, Eye, Play, ArrowRight, Search, List } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

// A helper to map string names to Lucide icons
const IconMap = {
    'book-open': BookOpen,
    'layers': Layers,
    'file-text': FileText,
    'lightbulb': Lightbulb,
    'play': Play,
    'default': BookOpen
};

const Learn = () => {
    const { categorySlug } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();

    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    // For root view
    const [rootCategories, setRootCategories] = useState([]);
    const [progressMap, setProgressMap] = useState({});

    // For category detail view
    const [category, setCategory] = useState(null);
    const [parentCategory, setParentCategory] = useState(null);
    const [childCategories, setChildCategories] = useState([]);
    const [articles, setArticles] = useState([]);
    const [categoryProgress, setCategoryProgress] = useState(null);

    useEffect(() => {
        if (categorySlug) {
            fetchCategoryDetail();
        } else {
            fetchRootCategories();
        }
    }, [categorySlug]);

    const fetchRootCategories = async () => {
        try {
            setLoading(true);
            const [catRes, progressRes] = await Promise.all([
                api.get('/api/learn/categories'),
                api.get('/api/learn/progress').catch(() => ({ data: [] }))
            ]);
            setRootCategories(catRes.data || []);
            setCategory(null);

            // Build slug -> progress_percent map
            const pMap = {};
            (progressRes.data || []).forEach(p => {
                pMap[p.category_slug] = p;
            });
            setProgressMap(pMap);
        } catch (error) {
            console.error('Failed to fetch root categories:', error);
            toast.error('Failed to load learning categories.');
        } finally {
            setLoading(false);
        }
    };

    const fetchCategoryDetail = async () => {
        try {
            setLoading(true);
            const [catRes, progressRes] = await Promise.all([
                api.get(`/api/learn/categories/${categorySlug}`),
                api.get('/api/learn/progress').catch(() => ({ data: [] }))
            ]);
            setCategory(catRes.data.category);
            setParentCategory(catRes.data.parent);
            setChildCategories(catRes.data.child_categories || []);
            setArticles(catRes.data.articles || []);

            // Find this category's progress from the progress array
            const catSlug = catRes.data.category?.slug;
            const pArr = progressRes.data || [];
            const found = pArr.find(p => p.category_slug === catSlug);
            setCategoryProgress(found || null);
        } catch (error) {
            console.error('Failed to fetch category detail:', error);
            toast.error('Failed to load category.');
            navigate('/learn');
        } finally {
            setLoading(false);
        }
    };

    const getIcon = (iconName) => {
        const ResolvedIcon = IconMap[iconName] || IconMap['default'];
        return <ResolvedIcon size={24} className="text-blue-600" />;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex flex-col">
                <NavigationHeader />
                <div className="flex-1 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-3">
                        <div className="w-8 h-8 rounded-full border-4 border-slate-200 border-t-blue-600 animate-spin" />
                        <span className="text-slate-500 font-medium">Loading content...</span>
                    </div>
                </div>
            </div>
        );
    }

    // ── Search Logic ───────────────────────────────────────────────────────────────────
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const filteredRootCategories = rootCategories.filter(cat =>
        cat.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cat.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cat.slug?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredChildCategories = childCategories.filter(cat =>
        cat.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cat.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredArticles = articles.filter(article =>
        article.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.summary?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // ── Root View (All Categories) ────────────────────────────────────────────────────────
    if (!categorySlug) {
        return (
            <div className="min-h-screen bg-white flex flex-col font-sans relative overflow-hidden">
                {/* Premium Header Background */}
                <div className="absolute top-0 left-0 right-0 h-[450px] bg-gradient-to-b from-emerald-50 via-emerald-50/20 to-white pointer-events-none z-0" />
                <NavigationHeader />

                <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-20 pb-24 relative z-10">
                    
                    {/* Hero Section */}
                    <div className="mb-12 text-center max-w-3xl mx-auto mt-6">
                        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-3">
                            Learn & Grow
                        </h1>
                        <p className="text-sm text-slate-600 leading-relaxed max-w-2xl mx-auto">
                            Master computer science concepts, system design, and interview strategies with our structured learning paths.
                        </p>
                        
                        {/* Search Bar */}
                        <div className="mt-10 relative max-w-xl mx-auto h-[52px]">
                            <div className="absolute inset-y-0 left-5 flex items-center pointer-events-none">
                                <Search size={18} className="text-slate-400" />
                            </div>
                            <input
                                type="text"
                                className="w-full h-full pl-12 pr-16 rounded-full border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all text-[15px] text-slate-700 placeholder:text-slate-400"
                                placeholder="Search concepts, topics, or articles..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                            />
                            <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none">
                                <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 bg-slate-50 text-[10px] font-bold text-slate-400">
                                    <span className="text-xs">⌘</span> K
                                </kbd>
                            </div>
                        </div>
                    </div>

                    {/* Grid */}
                    {filteredRootCategories.length === 0 ? (
                        <div className="text-center py-16 bg-white rounded-3xl border border-slate-200">
                            <Layers size={48} className="mx-auto text-slate-300 mb-4" />
                            <p className="text-lg font-normal text-slate-500">No categories found</p>
                            <p className="text-slate-500 mt-2 font-normal">{searchQuery ? `No matches for "${searchQuery}"` : 'Learning content is currently being prepared.'}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                            {filteredRootCategories.map((cat, index) => {
                                // Assign difficulty based on dummy mapping if field doesn't exist natively
                                const difficultyMap = {
                                    'dsa': 'ADVANCED',
                                    'system-design': 'INTERMEDIATE',
                                    'frontend': 'INTERMEDIATE',
                                    'backend': 'ADVANCED',
                                    'databases': 'BEGINNER',
                                    'operating-systems': 'INTERMEDIATE'
                                };
                                const difficulty = cat.difficulty || difficultyMap[cat.slug] || 'INTERMEDIATE';
                                
                                // Real progress from backend
                                const catProgress = progressMap[cat.slug];
                                const masteredProgress = catProgress?.progress_percent || 0;
                                const isMastered = masteredProgress > 0;
                                
                                return (
                                <Link
                                    key={cat.id}
                                    to={`/learn/${cat.slug}`}
                                    className="group relative bg-white flex flex-col justify-between rounded-xl border border-slate-200 overflow-hidden hover:border-emerald-200 hover:-translate-y-1 transition-all duration-300 h-full min-h-[380px]"
                                >
                                    <div className="p-8 flex-1 flex flex-col">
                                        <div className="flex justify-between items-start mb-8">
                                            <div className="text-emerald-500 group-hover:scale-110 transition-transform duration-300">
                                                {getIcon(cat.icon)}
                                            </div>
                                            <span className="bg-slate-100 text-slate-600 text-[9px]font-bold tracking-widest px-2.5 py-1.5 rounded-sm">
                                                {difficulty}
                                            </span>
                                        </div>
                                        
                                        <h3 className="text-lg font-bold text-slate-900 tracking-tight mb-2">
                                            {cat.name}
                                        </h3>
                                        
                                        <div className="flex items-center gap-2 text-[13px] font-medium text-slate-500 mb-5">
                                            <List size={14} className="text-slate-400 stroke-2" />
                                            {cat.child_count || 0} Topics
                                        </div>
                                        
                                        <p className="text-xs text-slate-600 leading-[1.6] mb-6 flex-1">
                                            {cat.description || "Explore structured content and ace your technical interviews with deep dives into advanced algorithms."}
                                        </p>
                                        
                                        <div className="mt-auto flex flex-col gap-3">
                                            <button className={`w-full py-3 rounded-lg border text-sm font-bold tracking-wide transition-colors ${
                                                isMastered 
                                                    ? 'border-emerald-500 text-emerald-600 hover:bg-emerald-50' 
                                                    : 'border-emerald-500 text-emerald-600 hover:bg-emerald-50'
                                            }`}>
                                                {isMastered ? 'Resume Path' : 'Start Learning'}
                                            </button>
                                            
                                            {/* Label below button */}
                                            {isMastered ? (
                                                <div className="flex items-center mt-1">
                                                    <span className="text-[9px] font-bold text-emerald-500tracking-widest">
                                                        {masteredProgress}% Mastered
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="h-4" /> // Spacer to keep card heights uniform somewhat
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Bottom Progress Bar */}
                                    <div className="h-1 w-full bg-slate-100 mt-auto">
                                        <div 
                                            className="h-full bg-emerald-500 transition-all duration-1000 ease-out" 
                                            style={{ width: `${masteredProgress}%` }}
                                        />
                                    </div>
                                </Link>
                            )})}
                        </div>
                    )}
                </main>
            </div>
        );
    }

    // ── Category Detail View ─────────────────────────────────────────────────────────────
    
    // Real progress data from backend
    const completedCount = categoryProgress?.completed_articles || 0;
    const totalCount = categoryProgress?.total_articles || articles.length || 0;
    const progressPercent = categoryProgress?.progress_percent || (totalCount > 0 ? Math.round(completedCount * 100 / totalCount) : 0);
    const masteryStats = {
        level: progressPercent >= 80 ? 'Advanced' : progressPercent >= 40 ? 'Intermediate' : 'Beginner',
        articlesCompleted: `${completedCount}/${totalCount}`,
        progress: progressPercent
    };

    return (
        <div className="min-h-screen bg-white flex flex-col font-sans relative overflow-hidden">
            {/* Premium Header Background */}
            <div className="absolute top-0 left-0 right-0 h-[450px] bg-gradient-to-b from-emerald-50 via-emerald-50/20 to-white pointer-events-none z-0" />
            <NavigationHeader />

            {/* Breadcrumb row */}
            <div className="pt-14 pb-2 px-6 max-w-7xl mx-auto w-full flex items-center justify-between relative z-10">
                <div className="flex items-center gap-2 text-[13px] font-medium text-slate-500 overflow-x-auto whitespace-nowrap hide-scrollbar pr-4">
                    <Link to="/dashboard" className="hover:text-slate-900 transition-colors shrink-0">
                        Dashboard
                    </Link>
                    <ChevronRight size={14} className="text-slate-300 stroke-[3] shrink-0" />
                    <Link to="/learn" className="hover:text-slate-900 transition-colors flex items-center gap-1.5 shrink-0">
                        <BookOpen size={14} /> Learn
                    </Link>
                    {parentCategory && (
                        <>
                            <ChevronRight size={14} className="text-slate-300 stroke-[3] shrink-0" />
                            <Link to={`/learn/${parentCategory.slug}`} className="hover:text-slate-900 transition-colors shrink-0">
                                {parentCategory.name}
                            </Link>
                        </>
                    )}
                    <ChevronRight size={14} className="text-slate-300 stroke-[3] shrink-0" />
                    <span className="text-slate-900 font-bold shrink-0">{category?.name}</span>
                </div>
                
                {/* Search Articles Input (Top Right) */}
                <div className="relative shrink-0 hidden md:block w-64">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <Search size={14} className="text-slate-400" />
                    </div>
                    <input
                        type="text"
                        className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 transition-all text-xs text-slate-700 placeholder:text-slate-400"
                        placeholder={`Search ${category?.name?.toLowerCase() || 'topic'}...`}
                        value={searchQuery}
                        onChange={handleSearchChange}
                    />
                </div>
            </div>

            <main className="flex-1 max-w-7xl mx-auto w-full px-6 pt-2 pb-20 relative z-10">
                
                {/* Topic Header & Progress Bar */}
                <div className="flex items-start gap-4 mb-6">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-end justify-between mb-2">
                            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                                {category?.name || 'Topic Name'}
                            </h1>
                            <span className="text-sm font-bold text-emerald-600 mb-1">
                                {masteryStats.progress}% Completed
                            </span>
                        </div>
                        
                        <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden mb-4">
                            <div 
                                className="h-full bg-emerald-500 transition-all duration-1000" 
                                style={{ width: `${masteryStats.progress}%` }} 
                            />
                        </div>
                        
                        <p className="text-sm text-slate-600 leading-relaxed max-w-3xl">
                            {category?.description || `Master complex relationships through advanced algorithms and theory.`}
                        </p>
                    </div>
                </div>

                {/* Mobile Search Articles Input */}
                <div className="mb-8 relative md:hidden">
                    <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                        <Search size={18} className="text-slate-400" />
                    </div>
                    <input
                        type="text"
                        className="w-full h-[46px] pl-12 pr-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all text-sm text-slate-700 placeholder:text-slate-400"
                        placeholder={`Search in ${category?.name?.toLowerCase() || 'topic'}...`}
                        value={searchQuery}
                        onChange={handleSearchChange}
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
                    
                    {/* Left Column: Articles List */}
                    <div className="lg:col-span-8 flex flex-col gap-4">
                        <div className="flex items-center justify-between mb-1 px-1">
                            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                                <FileText size={18} className="text-emerald-600 fill-emerald-100" /> 
                                {filteredArticles.length > 0 ? 'Articles' : filteredChildCategories.length > 0 ? 'Subtopics' : 'Articles'}
                            </h2>
                            <span className="text-[10px] font-bold text-slate-400tracking-widest">
                                {filteredArticles.length > 0 ? `${filteredArticles.length} MODULES` : filteredChildCategories.length > 0 ? `${filteredChildCategories.length} SUBTOPICS` : '0 MODULES'}
                            </span>
                        </div>

                        {/* Map actual fetched articles OR child categories */}
                        <div className="flex flex-col gap-4">
                            {filteredArticles.length === 0 && filteredChildCategories.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {filteredChildCategories.map((childCat, index) => (
                                        <Link 
                                            key={childCat.id} 
                                            to={`/learn/${childCat.slug}`} 
                                            className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col justify-between hover:border-emerald-300 hover:-translate-y-0.5 transition-all duration-300 group"
                                        >
                                            <div>
                                                <div className="flex items-center gap-3 mb-3">
                                                    <div className="text-emerald-500 group-hover:scale-110 transition-transform">
                                                        {getIcon(childCat.icon)}
                                                    </div>
                                                    <h3 className="text-base font-bold text-slate-900">{childCat.name}</h3>
                                                </div>
                                                <p className="text-xs text-slate-500 line-clamp-2 mb-4">
                                                    {childCat.description || `Explore modules and topics under ${childCat.name}.`}
                                                </p>
                                            </div>
                                            <div className="flex items-center justify-between mt-auto">
                                                <span className="text-xs font-medium text-slate-500 bg-slate-50 px-2 py-1 rounded">
                                                    {childCat.article_count || 0} Articles
                                                </span>
                                                <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                                                    <ArrowRight size={14} />
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            ) : (
                                filteredArticles.map((article, index) => {
                                // Use real progress status from backend, default to not_started
                                const status = article.progress_status || 'not_started';
                                const isCompleted = status === 'completed';

                                return (
                                    <div key={article.id} className="bg-white rounded-2xl border border-slate-200 p-6 flex items-start gap-5 hover:border-emerald-200 hover:-translate-y-0.5 transition-all duration-300">
                                        <div className="min-w-12 w-12 h-12 rounded-full bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-800 font-bold text-[15px] shrink-0 mt-0.5">
                                            {index + 1}
                                        </div>
                                        
                                        <div className="flex-1 w-full min-w-0">
                                            <div className="flex items-start justify-between gap-4 mb-2 -mt-0.5">
                                                <h3 className="text-[17px] font-bold text-slate-900 leading-snug pr-4">
                                                    {article.title}
                                                </h3>
                                                {isCompleted ? (
                                                    <span className="shrink-0 inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-600 text-[9px] font-boldtracking-wider px-2.5 py-1 rounded-sm">
                                                        <Icon name="CheckCircle2" size={10} className="stroke-[3]" /> COMPLETED
                                                    </span>
                                                ) : (
                                                    <span className="shrink-0 inline-flex items-center gap-1.5 bg-slate-100 text-slate-500 text-[9px] font-boldtracking-wider px-2.5 py-1 rounded-sm">
                                                        <Clock size={10} className="stroke-[3]" /> IN PROGRESS
                                                    </span>
                                                )}
                                            </div>
                                            
                                            <p className="text-[14px] text-slate-500 leading-[1.6] mb-5 pr-8">
                                                {article.summary || article.content?.substring(0, 100).replace(/<[^>]*>?/gm, "")}
                                            </p>
                                            
                                            <div className="flex items-center justify-between mt-auto">
                                                <div className="flex items-center gap-4 text-xs font-bold text-slate-400tracking-widest">
                                                    <span className="flex items-center gap-1.5 text-emerald-600/70"><Clock size={14} /> {article.read_time_minutes || 7} min read</span>
                                                    <span className="flex items-center gap-1.5"><Eye size={14} /> {(article.views_count || 1240).toLocaleString()} views</span>
                                                </div>
                                                
                                                <Link 
                                                    to={`/learn/${category?.slug || 'graphs'}/${article.slug || 'intro'}`} 
                                                    className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-600tracking-[0.15em] hover:text-emerald-700 transition-colors group/link"
                                                >
                                                    {isCompleted ? 'READ ARTICLE' : 'CONTINUE READING'} 
                                                    <ArrowRight size={14} className="group-hover/link:translate-x-1 transition-transform" />
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }))}
                            
                            {filteredArticles.length === 0 && filteredChildCategories.length === 0 && (
                                <div className="text-center py-10 bg-white rounded-xl border border-slate-200">
                                    <FileText size={32} className="mx-auto text-slate-300 mb-3" />
                                    <p className="text-sm font-normal text-slate-500">No content available</p>
                                    <p className="text-xs text-slate-500 mt-1 font-normal">{searchQuery ? `No matches for "${searchQuery}"` : 'Check back soon for new articles or topics.'}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Column: Sidebar Panels */}
                    <div className="lg:col-span-4 flex flex-col gap-6">
                        
                        {/* Topic Mastery Stats */}
                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                            <h3 className="text-[10px] font-bold text-slate-400tracking-[0.2em] mb-4">Topic Mastery</h3>
                            
                            <div className="flex flex-col gap-4 mb-5">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium text-slate-600">Current Level</span>
                                    <span className="bg-emerald-50 text-emerald-600 text-[10px] font-bold rounded px-2 py-0.5">
                                        {masteryStats.level}
                                    </span>
                                </div>
                                <div className="w-full h-[1px] bg-slate-100" />
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium text-slate-600">Articles Done</span>
                                    <span className="text-[13px] font-bold text-slate-900">
                                        {masteryStats.articlesCompleted}
                                    </span>
                                </div>
                            </div>
                            
                            <button className="w-full bg-[#0B1521] hover:bg-slate-800 text-white text-xs font-bold py-2.5 rounded-lg transition-colors">
                                View Detailed Stats
                            </button>
                        </div>
                        
                        {/* Quiz Banner Improvement */}
                        <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-xl relative overflow-hidden group border border-slate-800">
                            {/* Decorative background vectors */}
                            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full -mr-16 -mt-16 blur-2xl group-hover:bg-indigo-500/20 transition-all duration-700 pointer-events-none" />
                            <div className="absolute bottom-0 left-0 w-24 h-24 bg-emerald-500/5 rounded-full -ml-12 -mb-12 blur-2xl group-hover:bg-emerald-500/10 transition-all duration-700 pointer-events-none" />
                            
                            <div className="relative z-10">
                                <div className="flex items-center gap-2 text-emerald-400 mb-4">
                                    <Icon name="Zap" size={16} className="fill-emerald-400" />
                                    <span className="text-[11px] font-bold tracking-[0.2em] uppercase">Knowledge Check</span>
                                </div>
                                <h3 className="text-[18px] font-bold mb-2 tracking-tight text-white">Take a Quick Quiz</h3>
                                <p className="text-sm text-white/75 mb-6 leading-relaxed pr-2">
                                    Ready to test your knowledge? Take a short quiz to validate your understanding.
                                </p>
                                
                                <Link
                                    to={isAuthenticated ? `/learn/quiz/${category?.slug}` : `/login?redirect=${encodeURIComponent(`/learn/quiz/${category?.slug}`)}`}
                                    className="w-full bg-white hover:bg-slate-100 text-slate-900 text-xs font-blacktracking-widest py-3 rounded-xl transition-all shadow-lg active:scale-[0.98] flex items-center justify-center gap-2"
                                >
                                    {isAuthenticated ? 'Start Quiz →' : 'Login to take quiz'}
                                </Link>
                            </div>
                        </div>

                    </div>
                </div>
            </main>
        </div>
    );
};

export default Learn;
